import React, { useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShieldAlert, Loader2, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Blocking screen shown when the signed-in user must set a new password
 * (new account or admin-reset). Rendered in place of the app by ProtectedRoute
 * while `user.must_change_password` is true. On success it refreshes the auth
 * state, which clears the flag and lets the app render normally.
 */
export default function ForcePasswordChange() {
  const { user, checkAuth, logout } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const rules = [
    { ok: next.length >= 10, label: 'At least 10 characters' },
    { ok: /[a-z]/.test(next) && /[A-Z]/.test(next), label: 'Upper & lowercase letters' },
    { ok: /\d/.test(next), label: 'A number' },
    { ok: /[^A-Za-z0-9]/.test(next), label: 'A symbol (e.g. !@#$%)' },
  ];
  const allOk = rules.every(r => r.ok);
  const canSubmit = current && allOk && next === confirm && next !== current && !busy;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (next !== confirm) { setError('New passwords do not match'); return; }
    setBusy(true);
    try {
      await api.changePassword(current, next);
      toast.success('Password updated');
      await checkAuth();
    } catch (err) {
      setError(err.message || 'Could not update password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="force-password-change">
      <div className="w-full max-w-md glass-card rounded-2xl border border-border/50 p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h1 className="font-heading text-lg font-semibold text-foreground">Set a new password</h1>
            <p className="text-xs text-muted-foreground">Required before you continue, {user?.name || user?.email}</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3.5">
          <div>
            <Label className="text-xs text-muted-foreground">Current (temporary) password</Label>
            <Input
              type={show ? 'text' : 'password'}
              value={current}
              onChange={e => setCurrent(e.target.value)}
              className="mt-1"
              autoFocus
              data-testid="fpc-current-password"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">New password</Label>
            <div className="relative mt-1">
              <Input
                type={show ? 'text' : 'password'}
                value={next}
                onChange={e => setNext(e.target.value)}
                className="pr-9"
                data-testid="fpc-new-password"
              />
              <button type="button" onClick={() => setShow(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="fpc-toggle-visibility">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Confirm new password</Label>
            <Input
              type={show ? 'text' : 'password'}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              className="mt-1"
              data-testid="fpc-confirm-password"
            />
          </div>

          <ul className="grid grid-cols-2 gap-1.5 pt-1">
            {rules.map(r => (
              <li key={r.label} className={`flex items-center gap-1.5 text-[11px] ${r.ok ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                <CheckCircle2 className={`w-3 h-3 ${r.ok ? 'opacity-100' : 'opacity-30'}`} /> {r.label}
              </li>
            ))}
          </ul>

          {next && confirm && next !== confirm && (
            <p className="text-[11px] text-red-400" data-testid="fpc-mismatch">Passwords do not match</p>
          )}
          {error && <p className="text-xs text-red-400" data-testid="fpc-error">{error}</p>}

          <Button type="submit" disabled={!canSubmit} className="w-full" data-testid="fpc-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update password & continue'}
          </Button>
        </form>

        <button
          onClick={() => logout()}
          className="mt-4 w-full text-center text-xs text-muted-foreground hover:text-foreground"
          data-testid="fpc-logout"
        >
          Sign out instead
        </button>
      </div>
    </div>
  );
}
